import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { fetchAuthSession } from 'aws-amplify/auth'
import { AppSidebar } from 'components/Shell/AppSidebar/AppSidebar'
import SidebarMobile from '../../components/Sidebar/SidebarMobile'
import { SharedLayoutWrapper, DashboardContainer } from './SharedLayout.styled'
import useAuth from 'hooks/useAuth'
import { RootState } from 'store'
import { linksAdmin, linksClient } from 'constants/links'
import {
  filterLinksByPermission,
  getFirstAccessiblePath,
  isModuleActive,
  isPathAllowedForModules,
} from 'components/Shell/AppSidebar/navUtils'
import { NoAccess } from 'pages/NoAccess/NoAccess'
import { JustSpinner } from 'components/Loading/Loading'

export const SharedLayout = () => {
  const { isAdmin, isInitialized, user: authUser } = useAuth()
  const { isSidebarOpen } = useSelector((store: RootState) => store.globals)
  const { user: storeUser } = useSelector((state: RootState) => state.user)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false)
  const [accessReady, setAccessReady] = useState(false)

  const user = storeUser || authUser
  const policy = user?.currentContract?.role?.permissionPolicy

  useEffect(() => {
    let mounted = true
    if (isAdmin) {
      setIsCompanyAdmin(false)
      setAccessReady(true)
      return () => {
        mounted = false
      }
    }
    setAccessReady(false)
    fetchAuthSession()
      .then((session) => {
        const groups = session.tokens?.idToken?.payload['cognito:groups'] as string[] | undefined
        if (!mounted) return
        setIsCompanyAdmin(Boolean(groups?.includes('company_administrator')))
        setAccessReady(true)
      })
      .catch(() => {
        if (!mounted) return
        setIsCompanyAdmin(false)
        setAccessReady(true)
      })
    return () => {
      mounted = false
    }
  }, [isAdmin])

  const accessibleModules = useMemo(() => {
    if (isAdmin) return linksAdmin
    if (isCompanyAdmin) return linksClient
    return filterLinksByPermission(linksClient, policy)
  }, [isAdmin, isCompanyAdmin, policy])

  const firstPath = useMemo(() => getFirstAccessiblePath(accessibleModules), [accessibleModules])
  const hasNoAccess = accessReady && isInitialized && !isAdmin && !isCompanyAdmin && !firstPath

  useEffect(() => {
    if (!isInitialized || !accessReady) return

    if (isAdmin) {
      if (pathname === '/' || pathname === '/no-access') {
        navigate(getFirstAccessiblePath(linksAdmin) || '/resources-models', { replace: true })
      }
      return
    }

    if (isCompanyAdmin) {
      if (pathname === '/' || pathname === '/no-access') {
        navigate(getFirstAccessiblePath(linksClient) || '/dashboard', { replace: true })
      }
      return
    }

    if (!firstPath) {
      if (pathname !== '/no-access') navigate('/no-access', { replace: true })
      return
    }

    if (pathname === '/' || pathname === '/no-access') {
      navigate(firstPath, { replace: true })
      return
    }

    const isKnownModulePath = linksClient.some((module) => isModuleActive(module, pathname))
    if (isKnownModulePath && !isPathAllowedForModules(accessibleModules, pathname)) {
      navigate(firstPath, { replace: true })
    }
  }, [
    isAdmin,
    isCompanyAdmin,
    isInitialized,
    accessReady,
    firstPath,
    pathname,
    navigate,
    accessibleModules,
  ])

  if (!isInitialized || !accessReady) {
    return (
      <SharedLayoutWrapper>
        <JustSpinner />
      </SharedLayoutWrapper>
    )
  }

  if (hasNoAccess || pathname === '/no-access') {
    return (
      <SharedLayoutWrapper>
        <NoAccess />
      </SharedLayoutWrapper>
    )
  }

  let style = {}
  if (pathname === '/global-view') {
    style = { top: 0 }
  }

  return (
    <SharedLayoutWrapper>
      <AppSidebar />
      <SidebarMobile />
      <DashboardContainer style={style} sidebar={isSidebarOpen}>
        <Outlet />
      </DashboardContainer>
    </SharedLayoutWrapper>
  )
}
